import { ArrowPathIcon, FaceSmileIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { Flag } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { SymptomCheckerAssessmentMessageBubble, VideoModal } from '../components/chatbot/tabs/ConversationsTab';
import { ChatModeToggle } from '../components/common/ChatModeToggle';
import Modal from '../components/ui/Modal';
import { fetchImageUrl, getUserFullName } from '../helpers/helper';
import { getUser } from '../lib/storage';
import { agentInstanceService } from '../services/agentService';
import { chatbotService } from '../services/chatbotService';
import { sendMessageStreamApi } from '../services/chatService';
import { socketService } from '../services/socketService';
import { changeDocumentTitle, changeFavicon } from '../utility/document';
import { renderChatMarkdown } from '../utility/renderMarkdownText';
import { senderLabels } from './PublicChatWidget';

export type TabId = 'general' | 'themes' | 'knowledge' | 'playground' | 'connect' | 'conversations';
export type IngestFlow = 'symptom-checker' | 'knowledge-base' | 'knowledge-base-qa';

export interface TabDef {
  id: TabId;
  label: string;
  icon: React.ComponentType<any>;
  component: React.ComponentType<{ chatbot: any }>;
}

export type ThemeState = {
  userMsgBg: string;
  userMsgFont: string;
  botMsgBg: string;
  botMsgFont: string;
  sendBtn: string;
  bg: string;
  inputTextBg: string;
  inputTextFont: string;
  inputBorder: string;
  fontFamily: string;
  welcome: string;
  placeholder: string;
  headerBg: any;
  headerTextFont: any;
};

export enum Role {
  USER = 'user',
  STAFF = 'staff',
  ASSISTANT = 'assistant',
}

export enum AnsweredBy {
  BOT = 'bot',
  STAFF = 'staff',
}

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'staff';
  content: any;
  metadata?: any;
  senderType?: 'user' | 'agent' | 'staff' | 'system-message' | string;
  type?: 'normal' | 'assessment';
  createdAt?: string;
  hasAudits?: boolean;
  conversationId?: string;
  message?: string;
  tenantUserProfile?: any;
  userProfile?: any;
  agentInstanceId?: string;
};

export type ConversationMessage = {
  id: string;
  conversationId: string;
  senderType: 'user' | 'agent' | string;
  message: string;
  messageType?: string;
  createdAt: string;
};

interface ImageGroup {
  description: string;
  groupId: string;
  images: Array<{
    index: number;
    imageUrl: string;
    orderInGroup: number;
  }>;
}

interface RightSectionItem {
  id: number | string | null;
  title: string;
  descriptionLines: string[];
  image: string | null;
}

interface RightSectionData {
  sectionHeading: string;
  sectionCountStats: {
    currentValue: number;
    totalValue: number;
  };
  data: Array<RightSectionItem>;
  isShowSeparator?: boolean;
}

interface TenantDbFaqMetadata {
  flow?: string;
  agentTypeCode?: string;
  capabilityCode?: string;
  sqlQuery?: string;
  rows?: any[];
  rowsReturned?: number;
  availableInsights?: Array<{
    key: string;
    title: string;
    subtitle: string;
  }>;
  additionalInfo?: { accountNumber?: string;[key: string]: any };
  [key: string]: any;
}

type SortDirection = 'asc' | 'desc';

interface DynamicGridRow {
  __id: string;
  [key: string]: string;
}

const DEFAULT_INSIGHTS = [
  { key: 'all', title: 'All Entries', subtitle: 'Show everything' },
  { key: 'requirements', title: 'Requirements', subtitle: 'Requirement-related entries' },
  { key: 'deficiencies', title: 'Deficiencies', subtitle: 'Deficiency-related entries' },
  { key: 'status', title: 'Status', subtitle: 'Status-related entries' },
  { key: 'notices_sent_last_3_months', title: 'Notices sent in last 3 months', subtitle: 'Recent notice activity' },
  { key: 'notes_history_3_months', title: '3 months notes history', subtitle: 'Recent notes history' },
];

const normalizeInsightLabel = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getMetadataInsights = (metadata?: TenantDbFaqMetadata) => {
  const backendInsights = Array.isArray(metadata?.availableInsights) ? metadata?.availableInsights : [];
  if (!backendInsights?.length) return DEFAULT_INSIGHTS;

  return [
    { key: 'all', title: 'All Entries', subtitle: 'Show everything' },
    ...backendInsights.map((item) => ({
      key: String(item.key || '').trim(),
      title: item.title || normalizeInsightLabel(String(item.key || '')),
      subtitle: item.subtitle || 'Filtered entries',
    })),
  ];
};

const getRowInsight = (row: DynamicGridRow) => String(row.insight || '').trim().toLowerCase();

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const formatRightPanelValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '—';
    const looksLikeIsoDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed);
    if (looksLikeIsoDate) {
      const d = new Date(trimmed);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString();
    }
    return trimmed;
  }
  if (typeof value === 'number') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getDbRowTitle = (row: any, index: number): string => {
  if (row?.account) return String(row.account);
  if (row?.name) return String(row.name);
  return `Row ${index + 1}`;
};

const buildTenantDbRightSectionData = (userQuery: string, metadata: TenantDbFaqMetadata): RightSectionData[] => {
  const sections: RightSectionData[] = [];

  if (metadata.sqlQuery) {
    sections.push({
      sectionHeading: `Query for ${userQuery}`,
      sectionCountStats: { currentValue: 0, totalValue: 0 },
      data: [
        {
          id: 'executed-query',
          title: 'SQL Query',
          descriptionLines: metadata.sqlQuery
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean),
          image: null,
        },
      ],
    });
  }

  const rows = Array.isArray(metadata.rows) ? metadata.rows : [];
  // const previewRows = rows.slice(0, 10);
  const previewRows = rows;

  sections.push({
    isShowSeparator: true,
    sectionHeading: 'Database Result',
    sectionCountStats: {
      currentValue: previewRows.length,
      totalValue: typeof metadata.rowsReturned === 'number' ? metadata.rowsReturned : previewRows.length,
    },
    data:
      previewRows.length > 0
        ? previewRows.map((row, index) => {
          if (isPlainObject(row)) {
            return {
              id: `db-row-${index}`,
              title: getDbRowTitle(row, index),
              descriptionLines: Object.entries(row).map(([key, value]) => `${key}: ${formatRightPanelValue(value)}`),
              image: null,
            };
          }
          return {
            id: `db-row-${index}`,
            title: `Row ${index + 1}`,
            descriptionLines: [formatRightPanelValue(row)],
            image: null,
          };
        })
        : [
          {
            id: 'no-rows',
            title: 'No rows returned',
            descriptionLines: [
              metadata.additionalInfo?.accountNumber
                ? `Account: ${metadata.additionalInfo.accountNumber}`
                : 'No matching records found',
            ],
            image: null,
          },
        ],
  });

  return sections;
};

const buildKnowledgeBaseRightSectionData = (metadata: any): RightSectionData[] => {
  let relevantImages = metadata?.relevantImages;
  if (!relevantImages) return [];

  try {
    relevantImages = typeof relevantImages === 'string' ? JSON.parse(relevantImages) : relevantImages;
    if (!Array.isArray(relevantImages) || relevantImages.length === 0) return [];

    return relevantImages.map((el: any) => ({
      sectionHeading: el.sectionHeading,
      sectionCountStats: {
        currentValue: el.images?.length ?? 0,
        totalValue: 0,
      },
      data: (el.images ?? []).map((image: any) => ({
        id: image.imageIndex,
        title: image.aboutImage,
        descriptionLines: [],
        image: image.imageUrl,
      })),
    }));
  } catch (imgErr) {
    console.warn('Failed to parse knowledge-base highlights', imgErr);
    return [];
  }
};

function useQueryParam(name: string) {
  return new URLSearchParams(window.location.search).get(name) ?? undefined;
}

const ImageGallery: React.FC<{ images: ImageGroup[] }> = ({ images }) => {
  if (!images || images.length === 0) return null;

  return (
    <div className="space-y-6">
      {images.map((group) => (
        <div key={group.groupId} className="space-y-3">
          <div className="px-1">
            <h3 className="text-sm font-semibold text-gray-900">{group.description}</h3>
          </div>
          <div className="space-y-3">
            {group.images.map((img) => (
              <div
                key={`${group.groupId}-${img.index}`}
                className="w-full rounded-2xl border border-violet-400 bg-violet-50/60 px-4 py-4 shadow-[0_0_0_1px_rgba(139,92,246,0.35)] transition-shadow hover:shadow-md"
              >
                <img
                  src={img.imageUrl}
                  alt={group.description}
                  className="h-auto max-h-64 w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'default-image.png';
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export const AssessmentCarousel: React.FC<{ content: any[] }> = ({ content }) => {
  const [activeVideo, setActiveVideo] = React.useState<string | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) e.stopPropagation();
    };

    el.addEventListener('wheel', handleWheel, { passive: true });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  if (!content?.length) return null;

  return (
    <div className="mt-4">
      <div ref={scrollRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain scroll-smooth">
        {content.map((item, index) => (
          <div
            key={index}
            className="w-90 snap-start rounded-2xl border border-violet-200 bg-gradient-to-br from-white to-violet-50 p-5 shadow-sm"
          >
            <div className="prose prose-sm max-w-none">{renderChatMarkdown(item.formattedText)}</div>
            <div className="my-4 h-px bg-violet-100" />
            {!!item.metadata?.media?.length && (
              <div>
                <h4 className="mb-3 text-sm font-semibold text-gray-800">Recommended Videos</h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {item.metadata.media.map((video: any, vidIndex: number) => (
                    <div
                      key={vidIndex}
                      onClick={() => setActiveVideo(video.url)}
                      className="group relative aspect-video cursor-pointer overflow-hidden rounded-xl bg-black"
                    >
                      <video src={video.url} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" preload="metadata" />
                      <div className="absolute inset-0 bg-black/30 transition group-hover:bg-black/40" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/95 shadow-lg transition group-hover:scale-110">
                          <svg className="ml-0.5 h-5 w-5 fill-current text-violet-600" viewBox="0 0 20 20">
                            <path d="M6 4l10 6-10 6V4z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {activeVideo && <VideoModal videoSrc={activeVideo} onClose={() => setActiveVideo(null)} />}
    </div>
  );
};

const normalizeHeaderKey = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

const collectHeaders = (rows: DynamicGridRow[]): string[] => {
  const seen = new Set<string>();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (key !== '__id') seen.add(key);
    });
  });
  return Array.from(seen);
};

const toCSV = (rows: DynamicGridRow[]) => {
  const headers = collectHeaders(rows);
  const escape = (value: unknown) => {
    const csvText = String(value ?? '');
    return /[\n\r,"]/g.test(csvText) ? `"${csvText.split('\"').join('\"\"')}"` : csvText;
  };

  return [headers.join(','), ...rows.map((row) => headers.map((header) => escape(row[header])).join(','))].join('\n');
};

const downloadText = (filename: string, fileText: string) => {
  const blob = new Blob([fileText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const parseDynamicGridRow = (item: RightSectionItem, index: number): DynamicGridRow | null => {
  const row: DynamicGridRow = { __id: String(item.id ?? `row-${index}`) };
  let hasPairs = false;

  item.descriptionLines.forEach((line, lineIndex) => {
    const separatorIndex = line.indexOf(':');
    if (separatorIndex === -1) return;

    const rawKey = line.slice(0, separatorIndex).trim();
    const value = line.slice(separatorIndex + 1).trim();
    if (!rawKey) return;

    const headerKey = normalizeHeaderKey(rawKey || `Column ${lineIndex + 1}`);
    row[headerKey] = value || '—';
    hasPairs = true;
  });

  return hasPairs ? row : null;
};

const getDynamicCellValue = (row: DynamicGridRow, key: string) => row[key] ?? '—';


const PublicChatWidgetReports: React.FC = () => {
  const { chatbotId, clientId } = useParams<{ chatbotId: string; clientId: string }>();
  const [userId] = useState(`guest-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  const viewType = useQueryParam('view-type');
  const externalUserId = useQueryParam('external-user-id');
  const externalUserProfileId = useQueryParam('external-user-profile-id');
  const selectedConversationId = useQueryParam('conversation-id');
  const routeRole = useQueryParam('role') as 'staff' | 'user' | undefined;
  const slug = useQueryParam('slug');

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const user = getUser();
  const tenant = useMemo(() => user?.tenant ?? user?.tenantId, [user]);

  const [chatbot, setChatbot] = useState<any>(null);
  const [theme, setTheme] = useState<ThemeState | any>(null);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [imageGallery, setImageGallery] = useState<ImageGroup[]>([]);
  const [rightSectionHeading, setRightSectionHeading] = useState('Key Highlights');
  const [rightSectionData, setRightSectionData] = useState<RightSectionData[]>([]);
  const [activeRightSectionItemId, setActiveRightSectionItemId] = useState<number | string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [userTenantProfile, setUserTenantProfile] = useState<any>(null);
  const [answeredBy, setAnsweredBy] = useState<AnsweredBy>(AnsweredBy.BOT);
  const [isUserOnline, setIsUserOnline] = useState(false);
  const [userProfileConversation, setUserProfileConversation] = useState<any>(null);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [showAuditMenu, setShowAuditMenu] = useState(false);
  const [showAuditQuestion, setShowAuditQuestion] = useState(false);
  const [auditUserMessage, setAuditUserMessage] = useState<any>(null);
  const [auditTargetMessage, setAuditTargetMessage] = useState<any>(null);
  const [audit, setAudit] = useState<any>(null);
  const [agentInstances, setAgentInstances] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [gridSearch, setGridSearch] = useState('');
  const [activeInsightKey, setActiveInsightKey] = useState('all');
  const [sort, setSort] = useState<{ key: string; dir: SortDirection }>({ key: '', dir: 'asc' });

  const [tenantDbInsights, setTenantDbInsights] = useState<Array<{ key: string; title: string; subtitle: string }>>(DEFAULT_INSIGHTS);

  const convId = selectedConversationId || conversationId!
  const derivedRole = userTenantProfile?.profileData?.profiles?.[0]?.type;
  const role = routeRole || (derivedRole === 'student' ? Role.USER : derivedRole === 'admin' || derivedRole === 'staff' ? Role.STAFF : undefined);

  const fetchFaviconUrl = useCallback(async () => {
    if (tenant?.s3FaviconObjectData) {
      const url = await fetchImageUrl(tenant.s3FaviconObjectData);
      if (url) changeFavicon(url);
    }
  }, [tenant]);

  useEffect(() => {
    if (tenant) fetchFaviconUrl();
    changeDocumentTitle(tenant?.name || process.env.REACT_APP_APPLICATION_NAME || 'Agentic Platform');
  }, [tenant, fetchFaviconUrl]);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    const container = messagesContainerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages]);

  useEffect(() => {
    const handleResize = () => {
      const container = messagesContainerRef.current;
      if (container) container.scrollTop = container.scrollHeight;
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isSending && textareaRef.current) textareaRef.current.focus();
  }, [isSending]);

  const loadChatbot = useCallback(async () => {
    if (!chatbotId) {
      setError('Chatbot ID is required');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const resp = await chatbotService.getChatbotById(encodeURIComponent(chatbotId));
      const chatbotData = (resp as any)?.data ?? resp;
      setChatbot(chatbotData);
      
      senderLabels.bot = chatbotData?.botDisplayName || 'Bot';
      senderLabels.user = chatbotData?.userDisplayName || 'You';

      const themeConfig = chatbotData?.themeConfig?.settings || chatbotData?.themeSettings?.settings || chatbotData?.settings || {};
      const themeSettings = chatbotData?.themeSettings?.settings || chatbotData?.settings || {};

      setTheme({
        userMsgBg: themeConfig.userMessageBgColor || themeConfig.userMessageBg || 'linear-gradient(90deg, rgba(171, 202, 217, 1) 0%, rgba(84, 132, 197, 1) 100%)',
        userMsgFont: themeConfig.userMessageFontColor || themeConfig.userMessageFont || '#ffffff',
        botMsgBg: themeConfig.botMessageBgColor || themeConfig.botMsgBg || '#ffffff',
        botMsgFont: themeConfig.botMessageFontColor || themeConfig.botMsgFont || '#000000',
        sendBtn: themeConfig.sendButtonColor || themeConfig.sendBtn || themeSettings.sendButtonColor || themeSettings.sendBtn || '#3b82f6',
        bg: themeConfig.bgColor || themeConfig.bg || '#f0f6ff',
        inputTextBg: themeConfig.inputTextBgColor || themeConfig.inputTextBg || '#ffffff',
        inputTextFont: themeConfig.inputTextFontColor || themeConfig.inputTextFont || '#000000',
        inputBorder: themeConfig.inputFieldBorderColor || themeConfig.inputBorder || '#e5e7eb',
        fontFamily: themeConfig.fontFamily || 'Inter, sans-serif',
        welcome: themeConfig.welcomeMessage || themeConfig.welcome || 'Hi! How can I help you today?',
        placeholder: themeConfig.placeholderText || themeConfig.placeholder || 'Type your message...',
        headerBg: themeConfig.headerBgColor || 'linear-gradient(90deg, rgba(171, 202, 217, 1) 0%, rgba(84, 132, 197, 1) 100%)',
        headerTextFont: themeConfig.headerTextFont || '#ffffff',
      });

      setMessages([
        {
          id: 'welcome',
          role: 'assistant',
          content: themeConfig.welcomeMessage || themeConfig.welcome || 'Hi! How can I help you today?',
        },
      ]);
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to load chatbot', err);
      setError('Failed to load chatbot configuration');
      setIsLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => {
    if (chatbotId) loadChatbot();
  }, [chatbotId, loadChatbot]);

  useEffect(() => {
    if (!(chatbot && chatbotId && externalUserId && slug)) return;

    const agents = chatbot?.agents?.map((agent: any) => ({
      ...agent,
      parsedMetadata:
        typeof agent.agentInstance?.agentType?.metadata === 'string'
          ? JSON.parse(agent.agentInstance.agentType.metadata)
          : agent.agentInstance?.agentType?.metadata,
    }));

    const useClientProfile = agents?.some((agent: any) => agent.parsedMetadata?.useClientProfile === true) || false;
    if (!useClientProfile) return;

    const syncProfile = async () => {
      try {
        const lsTenantId = localStorage.getItem('agentictenantid') || localStorage.getItem('tenantid');
        const chatbotTenantId = chatbot?.tenantId || chatbot?.tenant?.id;
        const tenantId = chatbotTenantId || lsTenantId;
        setIsLoading(true);
        const userProfile = await chatbotService.syncTenantUserProfile({
          chatbotId,
          tenantId,
          externalUserId,
          externalUserProfileId,
          slug,
        });
        setUserTenantProfile(userProfile);
      } catch (err) {
        console.error('Failed to sync tenant user profile', err);
      } finally {
        setIsLoading(false);
      }
    };

    syncProfile();
  }, [chatbotId, chatbot, externalUserId, externalUserProfileId, slug]);

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  };

  const resetChat = () => {
    const welcomeMsg = theme?.welcome || 'Hi! How can I help you today?';
    setMessages([{ id: 'welcome', role: 'assistant', content: welcomeMsg }]);
    setConversationId(undefined);
    setInput('');
    setImageGallery([]);
    setRightSectionData([]);
    setGridSearch('');
    setActiveInsightKey('all');
    setTenantDbInsights(DEFAULT_INSIGHTS);
  };

  const loadConversation = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const conv: any = await chatbotService.singleConversationHistoryById(id);
      const newMessages = (conv?.messages ?? [])
        .slice()
        .sort((a: ConversationMessage, b: ConversationMessage) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .map((message: any) => ({
          ...message,
          metadata: typeof message.metadata === 'string' ? JSON.parse(message.metadata) : message.metadata,
        }));
      setConversationId(conv.id);
      setUserProfileConversation(conv.tenatUserProfile);
      setMessages(newMessages);

      const latestTenantDbMessage = [...newMessages]
        .reverse()
        .find(
          (msg: any) =>
            msg?.metadata?.capabilityCode === 'tenant-platform-db-faq' &&
            Array.isArray(msg?.metadata?.rows)
        );

      if (latestTenantDbMessage?.metadata) {
        setTenantDbInsights(getMetadataInsights(latestTenantDbMessage.metadata));
        setRightSectionData(
          buildTenantDbRightSectionData(
            latestTenantDbMessage?.message || latestTenantDbMessage?.content || 'Query',
            latestTenantDbMessage.metadata
          )
        );
      } else {
        setTenantDbInsights(DEFAULT_INSIGHTS);
      }
    } catch (err) {
      console.error('Failed to load conversation', err);
      setError('Failed to load conversation messages.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadLatestConversation = useCallback(async () => {
    try {
      if (!chatbotId) return;
      const conv: any = await chatbotService.listConversationHistory(chatbotId, {
        externalUserId,
        externalUserProfileId,
        limit: 1
      });
      if (!conv) return;
      const newMessages = (conv?.messages ?? [])
        .slice()
        .sort((a: ConversationMessage, b: ConversationMessage) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .map((message: any) => ({
          ...message,
          metadata: typeof message.metadata === 'string' ? JSON.parse(message.metadata) : message.metadata,
        }));
      setConversationId(conv.id);
      setMessages(newMessages);

      const latestTenantDbMessage = [...newMessages]
        .reverse()
        .find(
          (msg: any) =>
            msg?.metadata?.capabilityCode === 'tenant-platform-db-faq' &&
            Array.isArray(msg?.metadata?.rows)
        );

      if (latestTenantDbMessage?.metadata) {
        setTenantDbInsights(getMetadataInsights(latestTenantDbMessage.metadata));
        setRightSectionData(
          buildTenantDbRightSectionData(
            latestTenantDbMessage?.message || latestTenantDbMessage?.content || 'Query',
            latestTenantDbMessage.metadata
          )
        );
      } else {
        setTenantDbInsights(DEFAULT_INSIGHTS);
      }
    } catch (err) {
      console.error(err);
    }
  }, [chatbotId, externalUserId, externalUserProfileId]);

  const reloadConversation = useCallback(() => {
    if (selectedConversationId) loadConversation(selectedConversationId);
    else if (externalUserId || externalUserProfileId) loadLatestConversation();
  }, [selectedConversationId, externalUserId, externalUserProfileId, loadConversation, loadLatestConversation]);

  useEffect(() => {
    reloadConversation();
  }, [reloadConversation]);

  const openAuditMenu = async (message: any) => {
    const msgs = messages ?? [];
    let userMsg: any = null;
    const idx = msgs.findIndex((m: any) => m.id === message.id);

    if (idx > 0) {
      for (let i = idx - 1; i >= 0; i -= 1) {
        if (msgs[i]?.senderType === 'user') {
          userMsg = msgs[i];
          break;
        }
      }
    } else {
      const targetTime = message?.createdAt ? new Date(message.createdAt).getTime() : Number.NaN;
      userMsg = [...msgs]
        .filter((m: any) => m?.senderType === 'user' && m?.createdAt && new Date(m.createdAt).getTime() <= targetTime)
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ?? null;
    }

    if (!userMsg) {
      toast.error('No preceding user message found for this agent response.');
      return;
    }

    setAuditUserMessage(userMsg);
    setAuditTargetMessage(message);
    setAudit({
      conversationId: message.conversationId,
      userMessageId: userMsg.id,
      agentMessageId: message.id,
      correctedAnswer: message.message,
      chatbotId: chatbot?.id,
      reason: 'wrong response',
    });

    try {
      const res: any = await agentInstanceService.listAgentInstance?.({
        page: 1,
        limit: 50,
        isActive: true,
        chatBotId: chatbot?.id,
      });
      const raw = res?.data?.agentInstances || res?.agentInstances || res?.data || res;
      setAgentInstances(raw ?? []);
      setShowAuditMenu(true);
    } catch (err) {
      console.error('Failed to load agentInstances', err);
    }
  };

  const onNewMessageReceived = useCallback(
    (data: any) => {
      if (data.senderType === 'system') {
        setMessages((prev) => [...prev, data]);
        return;
      }
      if (role === Role.USER && data.answeredBy === AnsweredBy.BOT) {
        if (data.answeredBy === AnsweredBy.BOT) return;
      }
      setMessages((prev) => [...prev, data]);
    },
    [role]
  );

  const checkLiveStatus = useCallback(async (currentConvId: string) => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_CHAT_SOCKET_URL}/live/${encodeURIComponent(currentConvId)}`);
      if (!resp.ok) {
        setIsUserOnline(false);
        return;
      }
      const data = await resp.json();
      setIsUserOnline(!!data?.live);
      if (data?.answeredBy === 'staff') setAnsweredBy(AnsweredBy.STAFF);
      else if (data?.answeredBy === 'bot') setAnsweredBy(AnsweredBy.BOT);
    } catch {
      setIsUserOnline(false);
    }
  }, []);

  const handleSocketJoined = useCallback(
    (joinRole?: string) => {
      const effectiveRole = joinRole || role;
      if (!convId || !effectiveRole) return;

      socketService.connect();
      socketService.joinRoom({
        conversationId: convId,
        role: effectiveRole,
        userInfo: userTenantProfile,
      });
      socketService.onMessage(onNewMessageReceived);
      socketService.onAnsweredByChanged((data: any) => {
        if (!data?.conversationId || data.conversationId !== convId) return;
        if (data.answeredBy === 'staff' || data.answeredBy === 'bot' || data.answeredBy === 'user') {
          setAnsweredBy(data.answeredBy);
        }
      });
      checkLiveStatus(convId);
    },
    [role, convId, userTenantProfile, onNewMessageReceived, checkLiveStatus]
  );

  useEffect(() => {
    if (role === Role.USER && conversationId) handleSocketJoined();
    socketService.onAgentJoined?.(() => { });
    socketService.onAgentLeft?.(() => { });
    return () => socketService.disconnect();
  }, [conversationId, role, handleSocketJoined]);

  useEffect(() => {
    if (role !== Role.STAFF || !convId) return undefined;

    handleSocketJoined('staff-passive');
    const handleUserJoined = (data: any) => {
      if (!data?.conversationId || data.conversationId !== convId) return;
      setIsUserOnline(true);
    };
    const handleUserLeft = (data: any) => {
      if (!data?.conversationId || data.conversationId !== convId) return;
      setIsUserOnline(false);
    };
    socketService.onUserJoined?.(handleUserJoined);
    socketService.onUserLeft?.(handleUserLeft);
    return () => socketService.disconnect();
  }, [role, convId, handleSocketJoined]);

  useEffect(() => {
    if (role !== Role.STAFF || !selectedConversationId || !convId) return;
    if (answeredBy === AnsweredBy.STAFF) socketService.setRole({ conversationId: convId, role: 'staff' });
    else socketService.setRole({ conversationId: convId, role: 'staff-passive' });
  }, [answeredBy, role, selectedConversationId, convId]);

  const submitMultiAudit = async (toBeTrained: boolean) => {
    if (!auditTargetMessage || selectedAgents.length === 0) return;
    try {
      setLoading(true);
      for (const agentId of selectedAgents) {
        const body = {
          ...audit,
          agentInstanceId: agentId,
          toBeTrained,
        };
        await agentInstanceService.createConversationAudit(body);
      }
      reloadConversation();
      toast.success(`Audit submitted for ${selectedAgents.length} agents.`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit audit.');
    } finally {
      setLoading(false);
      setShowAuditMenu(false);
      setShowAuditQuestion(false);
      setAudit(null);
      setSelectedAgents([]);
    }
  };

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;

    if (answeredBy === AnsweredBy.STAFF && role) {
      const userMsg: ChatMessage = { id: `u-${Date.now()}`, role, content: trimmed };
      setMessages((prev) => [...prev, userMsg]);
      socketService.sendMessage({
        conversationId: convId,
        role,
        content: trimmed,
        userProfileId: userTenantProfile?.id,
        tenantUserProfile: userTenantProfile,
        senderType: role === Role.STAFF ? 'staff' : 'user',
      });
      setInput('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      return;
    }

    const lsTenantId = localStorage.getItem('agentictenantid') || localStorage.getItem('tenantid');
    const chatbotTenantId = chatbot?.tenantId || chatbot?.tenant?.id;
    const tenantId = chatbotTenantId || lsTenantId;
    if (!tenantId) throw new Error('Tenant ID is required but not found');

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    if (answeredBy === AnsweredBy.BOT) {
      socketService.sendMessage({
        conversationId: convId,
        role: 'user',
        content: trimmed,
        userProfileId: userTenantProfile?.id,
        tenantUserProfile: userTenantProfile,
        senderType: 'user',
      });
    }

    setIsSending(true);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    const streamingMsgId = `a-${Date.now()}`;
    const streamingMsg: ChatMessage = {
      id: streamingMsgId,
      role: 'assistant',
      content: '',
      metadata: { isStreamingPlaceholder: true },
    };

    setMessages((prev) => [...prev, streamingMsg]);

    const payload: any = {
      chatbotId,
      tenantId,
      userId,
      message: trimmed,
      viewType,
      additionalInputParams: {
        externalUserId,
        externalUserProfileId,
      },
      userTenantProfileId: userTenantProfile?.id,
    };
    if (clientId) payload.clientId = clientId;
    if (conversationId) payload.conversationId = conversationId;

    try {
      let response;
      try {
        response = await sendMessageStreamApi(payload, false);
      } catch (err: any) {
        if (err?.status === 401 || err?.status === 403) response = await sendMessageStreamApi(payload, true);
        else throw err;
      }

      if (!response.ok) throw new Error('Network error');
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let accumulatedContent = '';
      let buffer = '';
      let finalCompleteChunk: any = null;
      let hasReceivedFirstChunk = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!value) continue;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n');
        buffer = parts[parts.length - 1];

        for (let i = 0; i < parts.length - 1; i += 1) {
          const line = parts[i].trim();
          if (!line.startsWith('data:')) continue;

          try {
            const jsonStr = line.replace('data:', '').trim();
            const data: any = JSON.parse(jsonStr);
            const isAssessment = data.metadata?.flow === 'symptom-checker' || data.metadata?.isAssessment === true;

            if (data.type === 'content') {
              if (!hasReceivedFirstChunk) hasReceivedFirstChunk = true;
              accumulatedContent += data.content || '';
              const nextContent = accumulatedContent;
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === streamingMsgId
                    ? { ...msg, content: nextContent, metadata: data.metadata, type: isAssessment ? 'assessment' : 'normal' }
                    : msg
                )
              );
            } else if (data.type === 'complete') {
              finalCompleteChunk = data;
              const finalContent = data.content || accumulatedContent;
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === streamingMsgId
                    ? { ...msg, content: finalContent, metadata: data.metadata, type: isAssessment ? 'assessment' : 'normal' }
                    : msg
                )
              );

              if (answeredBy === AnsweredBy.BOT) {
                const convIdForSocket = data.conversationId || selectedConversationId || conversationId;
                if (convIdForSocket) {
                  socketService.sendMessage({
                    conversationId: convIdForSocket,
                    role: 'bot',
                    content: finalContent,
                  });
                }
              }

              if (!conversationId && data.conversationId) setConversationId(data.conversationId);

              if (data.metadata?.relevantImages) {
                try {
                  const parsedImages = typeof data.metadata.relevantImages === 'string'
                    ? JSON.parse(data.metadata.relevantImages)
                    : data.metadata.relevantImages;
                  if (Array.isArray(parsedImages)) setImageGallery(parsedImages);
                } catch (imgErr) {
                  console.warn('Failed to parse image gallery', imgErr);
                }
              }

              switch ((data?.metadata?.flow || null) as IngestFlow | null) {
                case 'symptom-checker': {
                  setRightSectionHeading('Key Highlights of Assessment');
                  setRightSectionData([]);
                  setActiveRightSectionItemId(null);
                  if (data.metadata.matchedConditions?.length) {
                    const matchingConditions = data.metadata.matchedConditions.sort((a: any, b: any) => b.relevanceScore - a.relevanceScore);
                    setRightSectionData([
                      {
                        sectionHeading: 'Matching Conditions',
                        sectionCountStats: { currentValue: matchingConditions.length, totalValue: 0 },
                        data: matchingConditions.map((condition: any) => ({
                          id: condition.docId,
                          title: condition.conditionName,
                          descriptionLines: [
                            `Probability: ${(condition.relevanceScore * 100).toFixed(0)}%`,
                            `Other symptoms: ${condition.symptomsText}`,
                          ],
                          image: null,
                        })),
                      },
                    ]);
                  }
                  break;
                }
                case 'knowledge-base': {
                  setRightSectionHeading('Key Highlights');
                  setActiveRightSectionItemId(null);
                  const metadata = data?.metadata;
                  if (metadata?.capabilityCode === 'tenant-platform-db-faq') {
                    setTenantDbInsights(getMetadataInsights(metadata));
                    setActiveInsightKey('all');
                    setRightSectionData(buildTenantDbRightSectionData(trimmed, metadata));
                  } else {
                    setTenantDbInsights(DEFAULT_INSIGHTS);
                    setRightSectionData(buildKnowledgeBaseRightSectionData(metadata));
                  }
                  break;
                }
                default:
                  setRightSectionHeading('Key Highlights');
                  setRightSectionData([]);
                  setActiveRightSectionItemId(null);
              }
            }
          } catch (parseErr) {
            console.warn('Frontend Parse error', parseErr);
          }
        }
      }

      if (!finalCompleteChunk && accumulatedContent) {
        const finalText = accumulatedContent;
        setMessages((prev) => prev.map((msg) => (msg.id === streamingMsgId ? { ...msg, content: finalText } : msg)));
      }
    } catch (err) {
      console.error('Failed to send message', err);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === streamingMsgId ? { ...msg, content: 'Sorry, I encountered an error. Please try again.' } : msg
        )
      );
    } finally {
      setIsSending(false);
    }
  };

  const baseGridRows = useMemo<DynamicGridRow[]>(() => {
    const dbSection = rightSectionData.find((section) => section.sectionHeading === 'Database Result');
    if (!dbSection) return [];

    return dbSection.data
      .map((item, index) => parseDynamicGridRow(item, index))
      .filter((row): row is DynamicGridRow => Boolean(row));
  }, [rightSectionData]);

  const dynamicColumns = useMemo(() => collectHeaders(baseGridRows), [baseGridRows]);

  useEffect(() => {
    setSort((current) => {
      if (current.key && dynamicColumns.includes(current.key)) return current;
      return { key: dynamicColumns[0] || '', dir: 'asc' };
    });
  }, [dynamicColumns]);

  const filteredRows = useMemo(() => {
    const query = gridSearch.trim().toLowerCase();

    return baseGridRows.filter((row) => {
      const matchesInsight =
        activeInsightKey === 'all' ? true : getRowInsight(row) === activeInsightKey;

      if (!matchesInsight) return false;

      if (!query) return true;

      return dynamicColumns.some((column: string) =>
        String(getDynamicCellValue(row, column)).toLowerCase().includes(query)
      );
    });
  }, [baseGridRows, dynamicColumns, gridSearch, activeInsightKey]);

  const sortedRows = useMemo(() => {
    if (!sort.key) return filteredRows;
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const av = String(getDynamicCellValue(a, sort.key));
      const bv = String(getDynamicCellValue(b, sort.key));
      const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [filteredRows, sort]);

  const rowCount = sortedRows.length;

  const toggleSort = (key: string) => {
    setSort((current) => (current.key !== key ? { key, dir: 'asc' } : { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const resetGridFilters = () => {
    setGridSearch('');
    setActiveInsightKey('all');
    setSort({ key: dynamicColumns[0] || '', dir: 'asc' });
  };

  const messageCards = messages.filter(
    (m: any) => (m.senderType || m.role) !== 'system' && (m.senderType || m.role) !== 'system-message'
  );

  if (isLoading) {
    return (
      <div className="h-screen w-full bg-[#f3f5fb] p-6">
        <div className="flex h-full items-center justify-center rounded-3xl border border-slate-200 bg-white text-sm text-gray-600 shadow-sm">
          Loading chat...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-full bg-[#f3f5fb] p-6">
        <div className="flex h-full items-center justify-center rounded-3xl border border-rose-200 bg-rose-50 text-sm text-red-600 shadow-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-slate-50" style={{ fontFamily: theme?.fontFamily || 'Inter, sans-serif' }}>
      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="flex flex-col gap-2">
          <div className="text-2xl font-semibold tracking-tight text-slate-900">
            {chatbot?.name || 'Public Chat Widget'} • Compliance Insights
          </div>
          <div className="text-sm text-slate-500">
            Ask a question. The agent fetches results and renders them in a sortable, filterable grid with export.
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-6">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-slate-900">{chatbot?.name || 'Ebix Assist'}</div>
                  <div className="mt-1 text-sm text-slate-500">Example: “What are my compliant accounts?”</div>
                </div>
                <div className="flex items-center gap-2">
                  {selectedConversationId && role === Role.STAFF && (
                    <ChatModeToggle
                      mode={answeredBy}
                      onChange={(mode: any) => socketService.setAnsweredBy({ conversationId: convId, answeredBy: mode })}
                    />
                  )}
                  <button
                    onClick={resetChat}
                    className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
                    aria-label="Reset chat"
                    title="Reset chat"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="px-6 py-5">
              <div ref={messagesContainerRef} className="h-[420px] overflow-auto rounded-2xl border border-slate-200 bg-slate-100/60 p-3">
                <div className="flex flex-col gap-3">
                  {messageCards.map((m: any, idx: number) => {
                    if (m.senderType === 'system-message' || m.senderType === 'system') {
                      const createdAtRaw = m.createdAt || m.metadata?.createdAt;
                      const createdAtDate = createdAtRaw ? new Date(createdAtRaw) : null;
                      const createdAtLabel = createdAtDate ? createdAtDate.toLocaleString() : '';
                      return (
                        <div key={m.id || idx} className="my-2 flex justify-center">
                          <div className="flex items-center gap-2 text-[11px] text-gray-500">
                            <span className="h-px w-10 bg-gray-200 sm:w-24" />
                            <span className="whitespace-nowrap font-medium">{m.content ?? m.message}</span>
                            {createdAtLabel ? <span className="ml-1 whitespace-nowrap text-gray-400">{createdAtLabel}</span> : null}
                            <span className="h-px w-10 bg-gray-200 sm:w-24" />
                          </div>
                        </div>
                      );
                    }

                    const sender = String(m.senderType || m.role || 'assistant').toLowerCase();
                    const isStaffViewer = role === Role.STAFF;
                    const isMine = isStaffViewer
                      ? sender === 'staff' || sender === 'agent' || sender === 'bot' || m.role === 'assistant'
                      : sender === 'user' || m.role === 'user';

                    const raw = m.content ?? m.message;
                    let parsedContent: any = raw;
                    try {
                      parsedContent = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    } catch {
                      parsedContent = raw;
                    }

                    const isAssessment = !!m.metadata && m.type === 'assessment';
                    const isStreamingPlaceholder = !!m.metadata?.isStreamingPlaceholder;
                    const isStringContent = ['string', 'number', 'boolean', 'undefined'].includes(typeof raw) || raw === null;
                    const messageContent = String(raw ?? '');
                    const senderType = m.senderType || m.role;
                    let senderLabel = 'Unknown';
                    let isAuditAllowed = false;

                    if (senderType === 'staff') {
                      const profile = m.tenantUserProfile || m.userProfile;
                      senderLabel = getUserFullName(profile?.profileData || profile) || 'Staff';
                      isAuditAllowed = true;
                    } else if (senderType === 'bot' || senderType === 'agent' || m.role === 'assistant') {
                      senderLabel = senderLabels.bot;
                      isAuditAllowed = true;
                    } else if (senderType === 'user') {
                      senderLabel = isMine
                        ? senderLabels.user
                        : getUserFullName(userProfileConversation?.profileData || userProfileConversation) || 'Student';
                    }

                    const createdAtRaw = m.createdAt || m.metadata?.createdAt;
                    const createdAtDate = createdAtRaw ? new Date(createdAtRaw) : null;
                    const createdAtLabel = createdAtDate ? createdAtDate.toLocaleString() : '';

                    return (
                      <div key={m.id || idx} className={`flex w-full items-center gap-3 ${isMine ? 'justify-end' : 'justify-start'}`}>
                        <div className={isMine ? 'w-full max-w-full overflow-hidden' : ''}>
                          <div
                            className="w-min-full rounded-xl border px-4 py-2.5 text-sm shadow-sm"
                            style={
                              isMine
                                ? {
                                  background: theme?.userMsgBg || 'linear-gradient(90deg, rgba(171, 202, 217, 1) 0%, rgba(84, 132, 197, 1) 100%)',
                                  color: theme?.userMsgFont || '#ffffff',
                                }
                                : {
                                  background: theme?.botMsgBg || '#ffffff',
                                  color: theme?.botMsgFont || '#000000',
                                  border: '1px solid #e5e7eb',
                                }
                            }
                          >
                            <div className="mb-1 flex items-center justify-between text-[11px] text-gray-500">
                              <span className="max-w-60 truncate font-medium">{senderLabel}</span>
                              {createdAtLabel ? <span className="ml-2 whitespace-nowrap">{createdAtLabel}</span> : null}
                            </div>
                            <div className="message-content space-y-6">
                              {isStreamingPlaceholder ? (
                                <div className="flex gap-1">
                                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>•</span>
                                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>•</span>
                                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>•</span>
                                </div>
                              ) : isAssessment ? (
                                <SymptomCheckerAssessmentMessageBubble metadata={m.metadata} />
                              ) : !isStringContent ? (
                                <pre className="overflow-auto whitespace-pre-wrap break-words text-xs">{JSON.stringify(parsedContent, null, 2)}</pre>
                              ) : (
                                renderChatMarkdown(messageContent)
                              )}
                            </div>
                          </div>
                        </div>
                        {role === Role.STAFF && isAuditAllowed ? (
                          <span
                            className="cursor-pointer text-slate-500 transition hover:text-violet-600"
                            onClick={() => {
                              if (!m.hasAudits) {
                                setSelectedAgents(m.agentInstanceId ? [m.agentInstanceId] : []);
                                setShowAuditMenu(false);
                                setShowAuditQuestion(true);
                                openAuditMenu(m);
                              } else {
                                toast.info('This message has already been audited.');
                              }
                            }}
                          >
                            <Flag size={16} fill={m.hasAudits ? '#1e3a8a' : 'none'} color={m.hasAudits ? '#1e3a8a' : 'currentColor'} />
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                  {isSending && !messageCards.some((m: any) => m.metadata?.isStreamingPlaceholder) && (
                    <div className="flex justify-start">
                      <div
                        className="max-w-80 rounded-xl border px-4 py-2.5 text-sm shadow-sm"
                        style={{
                          background: theme?.botMsgBg || '#ffffff',
                          color: theme?.botMsgFont || '#000000',
                          border: '1px solid #e5e7eb',
                        }}
                      >
                        <div className="flex gap-1">
                          <span className="animate-bounce" style={{ animationDelay: '0ms' }}>•</span>
                          <span className="animate-bounce" style={{ animationDelay: '150ms' }}>•</span>
                          <span className="animate-bounce" style={{ animationDelay: '300ms' }}>•</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 rounded-xl border px-3 py-2 shadow-sm" style={{ background: theme?.inputTextBg || '#ffffff', borderColor: theme?.inputBorder || '#e5e7eb' }}>
                <button className="flex-shrink-0 p-1 transition-opacity hover:opacity-70" style={{ color: theme?.botMsgFont || '#6b7280' }} aria-label="Emoji">
                  <FaceSmileIcon className="h-5 w-5" />
                </button>
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={handleInput}
                  rows={1}
                  placeholder={theme?.placeholder || 'Type your message...'}
                  disabled={isSending}
                  className="flex-1 resize-none overflow-auto bg-transparent outline-none focus:border-none focus:outline-none focus:ring-0 placeholder:text-sm"
                  style={{ maxHeight: '120px', color: theme?.inputTextFont || '#000000' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <button
                  onClick={sendMessage}
                  disabled={isSending || !input.trim()}
                  className="flex-shrink-0 rounded-lg p-2 text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
                  style={{ background: theme?.sendBtn || '#3b82f6' }}
                  aria-label="Send message"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="my-4 h-px bg-slate-200" />

              <div className="space-y-2">
                <div className="text-sm font-semibold text-slate-900">Insights</div>
                <div className="flex flex-wrap gap-2">
                  {tenantDbInsights.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setActiveInsightKey(item.key)}
                      className={`w-full rounded-2xl border px-4 py-3 text-left shadow-sm transition sm:w-auto ${activeInsightKey === item.key
                        ? 'border-violet-300 bg-violet-50/80'
                        : 'border-slate-200 bg-white hover:border-violet-200 hover:bg-slate-50'
                        }`}
                    >
                      <div className="flex items-start gap-2">
                        <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-violet-100 text-[11px] text-violet-600">✦</span>
                        <div>
                          <div className="font-semibold leading-tight text-slate-900">{item.title}</div>
                          <div className="mt-1 text-xs leading-tight text-slate-500">{item.subtitle}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* {rightSectionData.length > 0 && (
                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{rightSectionHeading}</div>
                      <div className="mt-1 text-sm text-slate-500">
                        {activeRightSectionItemId ? `Selected item: ${activeRightSectionItemId}` : `Live status: ${isUserOnline ? 'Online' : 'Offline'}`}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {rightSectionData.map((section, sectionIndex) => (
                      <div key={`${section.sectionHeading}-${sectionIndex}`}>
                        <div className="mb-3 flex items-center gap-2">
                          {section.sectionHeading?.trim().length ? (
                            <p className="text-sm font-semibold text-gray-900">{section.sectionHeading}</p>
                          ) : null}
                          {(section.sectionCountStats.currentValue || 0) !== 0 ? (
                            <span className="inline-flex h-6 items-center rounded-full bg-violet-50 px-2.5 text-xs font-medium text-violet-700">
                              {section.sectionCountStats.currentValue}
                              {(section.sectionCountStats.totalValue || 0) !== 0 ? ` / ${section.sectionCountStats.totalValue}` : ''}
                            </span>
                          ) : null}
                        </div>

                        <div className="space-y-3">
                          {section.data.map((item) => {
                            const isActive = activeRightSectionItemId === item.id;
                            return (
                              <button
                                key={String(item.id)}
                                type="button"
                                onClick={() => setActiveRightSectionItemId(item.id)}
                                className={`w-full rounded-2xl border px-4 py-4 text-left transition-all duration-200 ${isActive
                                  ? 'border-violet-400 bg-violet-50/60 shadow-[0_0_0_1px_rgba(139,92,246,0.35)]'
                                  : 'border-gray-200 bg-white hover:border-violet-200 hover:shadow-sm'
                                  }`}
                              >
                                <div className="flex items-stretch gap-4">
                                  {item.image ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedImage(item.image as string);
                                      }}
                                      className="group relative h-20 w-32 flex-shrink-0 overflow-hidden rounded-xl bg-gray-100 shadow-inner transition-transform hover:scale-105"
                                    >
                                      <img
                                        src={item.image}
                                        alt={item.title}
                                        className="h-full w-full object-cover"
                                        onError={(e) => {
                                          e.currentTarget.src = 'default-image.png';
                                        }}
                                      />
                                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
                                        <div className="opacity-0 transition-opacity group-hover:opacity-100">
                                          <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0zM13 10H7" />
                                          </svg>
                                        </div>
                                      </div>
                                    </button>
                                  ) : null}
                                  <div className="flex-1">
                                    <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                                    {item.descriptionLines.map((description) => (
                                      <p key={description} className="mt-1 text-xs leading-relaxed text-gray-500">
                                        {description}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        {section.isShowSeparator ? (
                          <div className="my-4 h-[3px] rounded-full bg-gradient-to-r from-transparent via-violet-300/80 to-transparent" />
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )} */}

              {imageGallery.length > 0 && <div className="mt-6"><ImageGallery images={imageGallery} /></div>}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-6">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="px-6 py-5">
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-slate-900">Results Grid</div>
                    <div className="mt-1 text-sm text-slate-500">{rowCount} rows • sort, filter, and export</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                      onClick={() => downloadText('accounts.csv', toCSV(sortedRows))}
                      disabled={sortedRows.length === 0}
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v1a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-1" />
                      </svg>
                      Download
                    </button>
                    {/* <button
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                      onClick={resetGridFilters}
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" />
                      </svg>
                      Reset
                    </button> */}
                  </div>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="relative flex-1">
                    <input
                      value={gridSearch}
                      onChange={(e) => setGridSearch(e.target.value)}
                      placeholder="Search visible table data…"
                      className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none transition focus:border-violet-300"
                    />
                    <svg className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="m21 21-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" />
                    </svg>
                  </div>
                  <div className="text-xs text-slate-500">Tip: click a column header to sort.</div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100/80">
                        <tr className="text-left">
                          {dynamicColumns.map((column: string) => (
                            <th
                              key={column}
                              className="cursor-pointer select-none whitespace-nowrap px-4 py-3 font-semibold text-slate-700"
                              onClick={() => toggleSort(column)}
                            >
                              <div className="inline-flex items-center gap-2">
                                {column}
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
                                </svg>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.map((row) => (
                          <tr key={row.__id} className="border-t border-slate-200 transition hover:bg-slate-50">
                            {dynamicColumns.map((column: string) => (
                              <td key={`${row.__id}-${column}`} className="whitespace-nowrap px-4 py-3 text-slate-700">
                                {getDynamicCellValue(row, column)}
                              </td>
                            ))}
                          </tr>
                        ))}
                        {sortedRows.length === 0 && (
                          <tr>
                            <td colSpan={Math.max(dynamicColumns.length, 1)} className="px-4 py-10 text-center text-slate-500">
                              No data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <div className="mt-1 text-sm text-slate-500">Mock behaviors:</div>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-500">
                    <li>Chat question triggers an “agent fetch” (simulated) and applies an Insight.</li>
                    <li>Insights are clickable bubbles that pre-fill the grid view.</li>
                    <li>Download exports the currently visible rows (post-filter + sort).</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSelectedImage(null)}>
          <div className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-lg bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute -right-3 -top-3 z-10 rounded-full bg-white p-1 transition-transform hover:scale-110 hover:shadow-xl"
              aria-label="Close image viewer"
            >
              <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={selectedImage}
              alt="Full view"
              className="max-h-[90vh] w-auto object-contain"
              onError={(e) => {
                e.currentTarget.src = 'default-image.png';
              }}
            />
            <a
              href={selectedImage}
              download
              className="absolute bottom-4 right-4 flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white shadow-lg transition-colors hover:bg-violet-700"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-1m-4-4-4 4m0 0-4-4m4 4V4" />
              </svg>
              Download
            </a>
          </div>
        </div>
      )}

      <Modal open={showAuditMenu} onClose={() => setShowAuditMenu(false)} title="Flag Response - Assign Agents" size="md" loading={loading}>
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div>
            <h3 className="mb-3 text-base font-semibold">Flag Response - Assign Agents</h3>
            <p className="mb-3 text-xs text-gray-600">Select one or more Agent Instances that should learn from this corrected response.</p>
            <div className="max-h-60 space-y-2 overflow-y-auto rounded-lg border p-2">
              {agentInstances.length === 0 ? (
                <div className="text-xs text-gray-500">No agent instances found.</div>
              ) : (
                agentInstances.map((ai: any) => (
                  <label key={ai.id} className="flex cursor-pointer items-center gap-3 rounded border p-2 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={selectedAgents.includes(ai.id)}
                      onChange={() =>
                        setSelectedAgents((prev) =>
                          prev.includes(ai.id) ? prev.filter((x) => x !== ai.id) : [...prev, ai.id]
                        )
                      }
                    />
                    <div>
                      <div className="text-sm font-medium">{ai.name}</div>
                      <div className="text-xs text-gray-500">{ai.id}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
            <div className="mt-5 flex justify-between">
              <button className="rounded-lg bg-gray-100 px-3 py-2 text-sm hover:bg-gray-200" onClick={() => setShowAuditMenu(false)} disabled={loading}>
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowAuditMenu(false);
                  setShowAuditQuestion(true);
                }}
                className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${selectedAgents.length === 0 ? 'cursor-not-allowed bg-gray-400' : ''}`}
                style={{ background: selectedAgents.length === 0 ? undefined : theme?.sendBtn || '#3b82f6', color: '#ffffff' }}
                disabled={selectedAgents.length === 0 || loading}
              >
                Submit Audit ({selectedAgents.length})
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={showAuditQuestion && Boolean(audit)} onClose={() => setShowAuditQuestion(false)} title="Flag Response - Provide Correct Answer" size="md" footer={null}>
        <div>
          <div>
            <label className="text-sm font-semibold text-gray-700">User Question</label>
            <textarea className="mt-1 w-full rounded-lg border bg-gray-100 p-2 text-sm" readOnly rows={2} value={auditUserMessage?.message || ''} />
          </div>
          <div className="mt-4">
            <label className="text-sm font-semibold text-gray-700">Bot Answer</label>
            <textarea className="mt-1 w-full rounded-lg border bg-gray-100 p-2 text-sm" rows={4} value={auditTargetMessage?.message || ''} readOnly />
          </div>
          <div className="mt-4">
            <label className="text-sm font-semibold text-gray-700">Correct Bot Answer (Edit below)</label>
            <textarea
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              rows={4}
              value={audit?.correctedAnswer || ''}
              onChange={(e) => setAudit((prev: any) => ({ ...prev, correctedAnswer: e.target.value }))}
            />
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button className="rounded-lg bg-gray-100 px-3 py-2 text-sm hover:bg-gray-200" onClick={() => setShowAuditQuestion(false)} disabled={loading}>
              Cancel
            </button>
            <button
              onClick={() => submitMultiAudit(false)}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${!audit?.correctedAnswer ? 'cursor-not-allowed bg-gray-400' : ''}`}
              style={{ background: !audit?.correctedAnswer ? undefined : theme?.sendBtn || '#3b82f6', color: '#ffffff' }}
              disabled={!audit?.correctedAnswer || loading}
            >
              Move to QA
            </button>
            <button
              onClick={() => submitMultiAudit(true)}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${!audit?.correctedAnswer ? 'cursor-not-allowed bg-gray-400' : ''}`}
              style={{ background: !audit?.correctedAnswer ? undefined : theme?.sendBtn || '#3b82f6', color: '#ffffff' }}
              disabled={!audit?.correctedAnswer || loading}
            >
              Move to QA + Train
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PublicChatWidgetReports;
